import React, { useEffect, useMemo, useState } from "react";
import { Download, KeyRound, Loader2 } from "lucide-react";
import SearchBar from "../components/CommonComponents/SearchInput";
import Pagination from "../components/CommonComponents/Pagination";
import TableActionButtons from "../components/CommonComponents/TableActionButtons";
import { tableContainer, tableBase, tableHead, tableHeader, tableCell, tableRow } from "../utils/tableStyle";
import CustomerBranchesModal from "./CustomerBranchesModal";

// Modals
import AddCustomerModal from "../components/superadmin/AddCustomerModal";
import DeleteModal from "../components/CommonComponents/ConfirmationModal";

// Toast utils
import { showSuccessToast, showErrorToast, showWarnToast } from "../utils/toast";
import { resolveAssetUrl } from "../utils/assetUrl";
import { normalizePosType } from "../utils/posTypeModules";

// API calls (services layer)
import { downloadCustomerImages, fetchCustomerImageBackupStatus, fetchCustomers, resetCustomerAdminPassword, updateCustomerStatus } from "../services/customerApi";
import { fetchBranches } from "../services/branchApi";

const formatDate = (value) => {
  if (!value) return "Not set";
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return "Not set";
  return new Date(year, month - 1, day).toLocaleDateString("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const toLocalDateKey = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const dateKeyToLocalDate = (dateKey) => {
  const [year, month, day] = String(dateKey || "").slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const getSubscriptionCountdown = (endDate) => {
  const endDateKey = endDate ? String(endDate).slice(0, 10) : "";
  const end = dateKeyToLocalDate(endDateKey);
  if (!end) {
    return {
      label: "No end date",
      className: "text-slate-500",
    };
  }

  const today = dateKeyToLocalDate(toLocalDateKey());
  const daysLeft = Math.round((end.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));

  if (daysLeft < 0) {
    const daysExpired = Math.abs(daysLeft);
    return {
      label: `Expired ${daysExpired} day${daysExpired === 1 ? "" : "s"} ago`,
      className: "text-rose-600",
    };
  }

  if (daysLeft === 0) {
    return {
      label: "Ends today",
      className: "text-amber-600",
    };
  }

  return {
    label: `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`,
    className: daysLeft <= 5 ? "text-amber-600" : "text-slate-500",
  };
};

const getBillingPeriodBadge = (endDate) => {
  const endDateKey = endDate ? String(endDate).slice(0, 10) : "";
  const [year, month, day] = endDateKey.split("-").map(Number);
  if (!year || !month || !day) {
    return {
      label: "Not Set",
      className: "border-slate-200 bg-slate-50 text-slate-500",
    };
  }

  if (toLocalDateKey() > endDateKey) {
    return {
      label: "Expired",
      className: "border-rose-100 bg-rose-50 text-rose-700",
    };
  }

  return {
    label: "Active",
    className: "border-emerald-100 bg-emerald-50 text-emerald-700",
  };
};

const getTenantBaseDomain = () => {
  const configured = String(process.env.REACT_APP_TENANT_BASE_DOMAIN || "").trim();
  if (configured) return configured;
  const labels = window.location.hostname.split(".");
  if (labels.length >= 3) return labels.slice(-2).join(".");
  return "zeroone-apps.com";
};

const getCustomerWorkspaceUrl = (subdomain) =>
  subdomain ? `https://${subdomain}.${getTenantBaseDomain()}` : "";

const CustomerList = () => {
  // ================= STATE =================
  const [customers, setCustomers] = useState([]); // Customer list
  const [selectedCustomer, setSelectedCustomer] = useState(null); // For edit/toggle
  const [branchCustomer, setBranchCustomer] = useState(null);
  const [branchCounts, setBranchCounts] = useState({});
  const [selectedUserId, setSelectedUserId] = useState(null); // For delete
  const [passwordResetTarget, setPasswordResetTarget] = useState(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [backupCustomerId, setBackupCustomerId] = useState(null);
  const [backupStatus, setBackupStatus] = useState("");
  const [imageBackupStatuses, setImageBackupStatuses] = useState({});
  const [imageBackupStatusError, setImageBackupStatusError] = useState(false);

  const handleImageBackup = async (customer) => {
    if (backupCustomerId !== null) return;
    setBackupCustomerId(customer.id);
    setBackupStatus(`Preparing image backup for ${customer.name}...`);
    try {
      const response = await downloadCustomerImages(customer.id);
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      const serverFilename = response.headers["content-disposition"]?.match(/filename="([^"]+)"/)?.[1];
      anchor.href = url;
      anchor.download = serverFilename || `customer-${customer.id}-images-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      const count = response.headers["x-backup-file-count"];
      const warnings = Number(response.headers["x-backup-warning-count"] || 0);
      const message = warnings
        ? `${customer.name}: backup download started with ${warnings} missing or skipped image references. Review manifest.json in the ZIP.`
        : `${customer.name}: backup download started${count !== undefined ? ` (${count} images)` : ""}. Original filenames and folders are preserved.`;
      setBackupStatus(message);
      if (warnings) showWarnToast(message);
      else showSuccessToast(message);
    } catch (error) {
      let message = error.response?.data?.message;
      if (error.response?.data instanceof Blob) {
        try { message = JSON.parse(await error.response.data.text()).message; } catch { /* Use fallback below. */ }
      }
      message = message || "Unable to download image backup. Please try again.";
      setBackupStatus(message);
      showErrorToast(message);
    } finally {
      setBackupCustomerId(null);
    }
  };

  // Modal state manager
  const [modal, setModal] = useState({
    addEdit: false,
    delete: false,
    toggle: false,
    resetPassword: false,
  });

  // Pagination + search
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const customersPerPage = 5;

  // ================= API CALLS =================
  const loadCustomers = async () => {
    try {
      const response = await fetchCustomers(); // call API service
      setCustomers(response.data); // save into state
    } catch (error) {
      console.error("Error fetching customers:", error);
      showErrorToast("Failed to fetch customer list.");
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await updateCustomerStatus(selectedUserId, "DELETED");

      // Optimistically update UI without full reload
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === selectedUserId ? { ...c, status: "DELETED" } : c
        )
      );

      setModal((prev) => ({ ...prev, delete: false }));
      showSuccessToast("Customer marked as DELETED.");
    } catch (error) {
      console.error("Error deleting customer:", error);
      showErrorToast("Failed to delete customer.");
    }
  };

  const handleToggleStatus = async () => {
    try {
      const newStatus =
        selectedCustomer.status.toUpperCase() === "ACTIVE"
          ? "INACTIVE"
          : "ACTIVE";

      await updateCustomerStatus(selectedCustomer.id, newStatus);

      // Update only that customer in state
      setCustomers((prev) =>
        prev.map((c) =>
          c.id === selectedCustomer.id ? { ...c, status: newStatus } : c
        )
      );

      setModal((prev) => ({ ...prev, toggle: false }));
      showSuccessToast(`Customer status set to ${newStatus}.`);
    } catch (error) {
      console.error("Error toggling status:", error);
      showErrorToast("Failed to update status.");
    }
  };

  const handleOpenPasswordReset = (customer, admin) => {
    setPasswordResetTarget({ customer, admin });
    setModal((prev) => ({ ...prev, resetPassword: true }));
  };

  const handleClosePasswordReset = () => {
    setPasswordResetTarget(null);
    setModal((prev) => ({ ...prev, resetPassword: false }));
  };

  const handleConfirmPasswordReset = async () => {
    if (!passwordResetTarget?.customer?.id || !passwordResetTarget?.admin?.id) return;

    setIsResettingPassword(true);
    try {
      const response = await resetCustomerAdminPassword(
        passwordResetTarget.customer.id,
        passwordResetTarget.admin.id
      );
      showSuccessToast(response.data?.message || "Temporary password sent to the admin email.");
      handleClosePasswordReset();
    } catch (error) {
      console.error("Error resetting admin password:", error);
      showErrorToast(error.response?.data?.message || "Failed to reset admin password.");
    } finally {
      setIsResettingPassword(false);
    }
  };

  // ================= HANDLERS =================
  const handleEdit = (customer) => {
    setSelectedCustomer(customer);
    setModal((prev) => ({ ...prev, addEdit: true }));
  };

  const handleAdd = () => {
    setSelectedCustomer(null); // new customer
    setModal((prev) => ({ ...prev, addEdit: true }));
  };

  const handleCloseModal = () => {
    setSelectedCustomer(null);
    setModal((prev) => ({ ...prev, addEdit: false }));
  };

  const handleUserAddedOrUpdated = () => {
    loadCustomers(); // reload after add/edit
    handleCloseModal();
  };

  // ================= FILTER & PAGINATION =================
  const filteredCustomers = useMemo(() => {
    const normalizedSearch = String(searchTerm || "").toLowerCase();
    return customers.filter((customer) =>
      (customer.name || "").toLowerCase().includes(normalizedSearch)
      || (customer.subdomain || "").toLowerCase().includes(normalizedSearch)
      || (customer.adminUsers || []).some((admin) =>
        (admin.username || "").toLowerCase().includes(normalizedSearch)
        || (admin.email || "").toLowerCase().includes(normalizedSearch)
      )
    );
  }, [customers, searchTerm]);

  const indexOfLast = currentPage * customersPerPage;
  const indexOfFirst = indexOfLast - customersPerPage;
  const currentCustomers = useMemo(
    () => filteredCustomers.slice(indexOfFirst, indexOfLast),
    [filteredCustomers, indexOfFirst, indexOfLast]
  );
  const totalPages =
    Math.ceil(filteredCustomers.length / customersPerPage) || 1;

  useEffect(() => {
    let disposed = false;
    let checking = false;
    const refresh = async () => {
      if (checking || document.hidden || !currentCustomers.length) return;
      checking = true;
      try {
        const response = await fetchCustomerImageBackupStatus(currentCustomers.map(customer => customer.id));
        if (!disposed) {
          setImageBackupStatuses(previous => ({ ...previous, ...response.data }));
          setImageBackupStatusError(false);
        }
      } catch {
        if (!disposed) setImageBackupStatusError(true);
      } finally { checking = false; }
    };
    refresh();
    const timer = setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      disposed = true;
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [currentCustomers, backupCustomerId]);


  // ================= LIFECYCLE =================
  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadBranchCounts = async () => {
      const activeCustomers = currentCustomers.filter((customer) => customer.status !== "DELETED");
      if (activeCustomers.length === 0) return;

      const counts = {};
      await Promise.all(
        activeCustomers.map(async (customer) => {
          try {
            const response = await fetchBranches(customer.id);
            const branches = response.data?.data || response.data || [];
            counts[customer.id] = branches.filter((branch) => branch.status === "ACTIVE").length;
          } catch (error) {
            counts[customer.id] = 0;
          }
        })
      );

      if (isMounted) {
        setBranchCounts((prev) => ({ ...prev, ...counts }));
      }
    };

    loadBranchCounts();

    return () => {
      isMounted = false;
    };
  }, [currentCustomers]);

  // ================= RENDER =================
  return (
    <div className="px-6 py-10 max-w-7xl mx-auto min-h-screen" style={{ background: 'var(--color-hover)' }}>
      {/* Search + Add Button */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
        <SearchBar
          placeholder="Search by name..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
        />

        <button
          onClick={handleAdd}
          className="btn-primary px-6 py-2 rounded-md font-medium shadow-md transition focus:outline-none"
        >
          + Add Customer
        </button>
      </div>

      {/* Title */}
      <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--color-text)' }}>Customers List</h2>

      {/* Table */}
      <div className={tableContainer}>
        <table className={tableBase}>
          <thead className={tableHead} style={{ background: 'var(--color-primary)' }}>
            <tr>
              <th className={tableHeader + ' text-left'}>#</th>
              <th className={tableHeader + ' text-left'}>Logo</th>
              <th className={tableHeader + ' text-left'}>Name</th>
              <th className={tableHeader + ' text-left'}>Workspace</th>
              <th className={tableHeader + ' text-left'}>Admin Users</th>
              <th className={tableHeader + ' text-left'}>POS Type</th>
              <th className={tableHeader + ' text-left'}>Subscription</th>
              <th className={tableHeader + ' text-left'}>Billing Period</th>
              <th className={tableHeader + ' text-left'}>Status</th>
              <th className={tableHeader + ' text-center'}>Branches</th>
              <th className={tableHeader + ' text-left'}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCustomers.map((customer, index) => (
              <tr
                key={customer.id}
                className={tableRow + ' border-b last:border-0' + (customer.status === "DELETED" ? " opacity-50" : "")}
              >
                <td className={tableCell}>{indexOfFirst + index + 1}</td>
                <td className={tableCell}>
                  {customer.logo ? (
                    <img
                      src={resolveAssetUrl(customer.logo)}
                      alt={`${customer.name} logo`}
                      className="w-10 h-10 object-contain rounded bg-white border"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className={tableCell + ' font-semibold'}>
                  {customer.name}
                </td>
                <td className={tableCell}>
                  {customer.subdomain ? (
                    <a
                      href={getCustomerWorkspaceUrl(customer.subdomain)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-w-[180px] flex-col rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs transition hover:border-slate-300 hover:bg-white"
                    >
                      <span className="font-bold text-slate-900">{customer.subdomain}</span>
                      <span className="mt-0.5 truncate text-slate-500">
                        {getCustomerWorkspaceUrl(customer.subdomain)}
                      </span>
                    </a>
                  ) : (
                    <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">Not set</span>
                  )}
                </td>
                <td className={tableCell}>
                  {customer.adminUsers?.length ? (
                    <div className="flex min-w-[220px] flex-col gap-2">
                      {customer.adminUsers.map((admin) => (
                        <div
                          key={admin.id}
                          className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm"
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm font-bold text-slate-900">{admin.username}</div>
                            <div className="truncate text-xs text-slate-500">{admin.email || "No email"}</div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleOpenPasswordReset(customer, admin)}
                            disabled={customer.status === "DELETED" || !admin.email}
                            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-600 shadow-sm transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-45"
                            title={admin.email ? "Reset password and email temporary password" : "Admin email is required"}
                          >
                            <KeyRound className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700">No admin user</span>
                  )}
                </td>
                <td className={tableCell}>{normalizePosType(customer.system_type)}</td>
                <td className={tableCell}>
                  {customer.subscriptionPackage ? (
                    <div className="min-w-[160px]">
                      <div className="font-semibold text-slate-900">{customer.subscriptionPackage.code}</div>
                      <div className="text-xs text-slate-500">
                        ₱{Number(customer.subscriptionPackage.base_monthly_amount || 0).toLocaleString()}/mo · ₱{Number(customer.subscriptionPackage.additional_user_monthly_amount || 0).toLocaleString()}/user
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-300">—</span>
                  )}
                </td>
                <td className={tableCell}>
                  <div className="min-w-[190px]">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-semibold text-slate-900">
                        {formatDate(customer.subscription_start_date)}
                      </span>
                      {(() => {
                        const badge = getBillingPeriodBadge(customer.subscription_end_date);
                        return (
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.08em] ${badge.className}`}>
                            {badge.label}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="text-xs text-slate-500">
                      to {formatDate(customer.subscription_end_date)}
                    </div>
                    {(() => {
                      const countdown = getSubscriptionCountdown(customer.subscription_end_date);
                      return (
                        <div className={`mt-1 text-xs font-semibold ${countdown.className}`}>
                          {countdown.label}
                        </div>
                      );
                    })()}
                  </div>
                </td>
                <td className={tableCell}>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${customer.status === "ACTIVE"
                      ? "bg-green-100 text-green-600"
                      : customer.status === "INACTIVE"
                        ? "bg-gray-200 text-gray-400"
                        : "bg-red-100 text-red-600"
                      }`}
                  >
                    {customer.status}
                  </span>
                </td>
                <td className={tableCell + ' text-center'}>
                  <button
                    type="button"
                    onClick={() => setBranchCustomer(customer)}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={customer.status === "DELETED"}
                  >
                    <span>Manage</span>
                    <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
                      {branchCounts[customer.id] ?? 0}
                    </span>
                  </button>
                </td>

                <td className={tableCell}>
                  <TableActionButtons
                    canEdit={customer.status !== "DELETED"}
                    canDelete={customer.status !== "DELETED"}
                    canToggle={customer.status !== "DELETED"}
                    onEdit={() => handleEdit(customer)}
                    onDelete={() => {
                      setSelectedUserId(customer.id);
                      setModal((prev) => ({ ...prev, delete: true }));
                    }}
                    onToggle={() => {
                      setSelectedCustomer(customer);
                      setModal((prev) => ({ ...prev, toggle: true }));
                    }}
                    toggleStatus={customer.status}
                  />
                  <button
                    type="button"
                    onClick={() => handleImageBackup(customer)}
                    disabled={backupCustomerId !== null}
                    aria-label={`Download image backup for ${customer.name}${imageBackupStatuses[customer.id]?.needs_backup ? '. Product image backup needed' : ''}`}
                    title={imageBackupStatuses[customer.id]?.last_download_at
                      ? `Last download: ${new Date(imageBackupStatuses[customer.id].last_download_at).toLocaleString()}. Download images with original filenames and folders.`
                      : "Download images with original filenames and folders. No download recorded yet."}
                    className="mt-2 inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-semibold disabled:cursor-wait disabled:opacity-50"
                    style={{ borderColor: "var(--color-border)", color: "var(--color-text)" }}
                  >
                    {backupCustomerId === customer.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                    {backupCustomerId === customer.id ? "Preparing backup..." : "Download images"}
                    {imageBackupStatuses[customer.id]?.needs_backup && (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-100 px-2 py-1 text-[10px] font-semibold text-amber-900">
                        <span className="relative inline-flex h-2 w-2" aria-hidden="true">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-amber-500 opacity-75 motion-safe:animate-ping" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-600" />
                        </span>
                        {imageBackupStatuses[customer.id].has_previous_backup ? "Images changed" : "Backup needed"}
                      </span>
                    )}
                  </button>
                </td>
              </tr>
            ))}
            {currentCustomers.length === 0 && (
              <tr>
                <td colSpan="11" className={tableCell + ' text-center py-8'}>
                  No customers found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <p role="status" className="mt-3 text-sm" style={{ color: "var(--color-muted)" }}>
        {backupStatus || "Image backups include customer-linked uploads with original filenames and folders. Each ZIP includes restore instructions and a report of missing files."}
      </p>
      <p className="mt-1 text-xs" style={{ color: "var(--color-muted)" }}>
        {imageBackupStatusError
          ? "Backup status is unavailable. Any badges shown are from the last successful check; downloads remain available."
          : "The amber ripple marks product images changed since the last download, or images with no recorded download. Missing/skipped files do not keep it on. Status refreshes every 15 seconds while this page is visible."}
      </p>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />

      {/* Modals */}
      {modal.addEdit && (
        <AddCustomerModal
          editData={selectedCustomer}
          onClose={handleCloseModal}
          onUserAddedOrUpdated={handleUserAddedOrUpdated}
        />
      )}

      <DeleteModal
        isOpen={modal.delete}
        onClose={() => setModal((prev) => ({ ...prev, delete: false }))}
        onConfirm={handleConfirmDelete}
        title="Delete Customer"
        message={`Are you sure you want to delete "${customers.find((c) => c.id === selectedUserId)?.name
          }"?`}
        confirmText="Yes"
        cancelText="No"
        type="delete"
      />

      <DeleteModal
        isOpen={modal.toggle}
        onClose={() => setModal((prev) => ({ ...prev, toggle: false }))}
        onConfirm={handleToggleStatus}
        title="Change Status"
        message={`Are you sure you want to set this customer to ${selectedCustomer?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"
          }?`}
        confirmText="Yes"
        cancelText="No"
        type="delete"
      />

      <DeleteModal
        isOpen={modal.resetPassword}
        onClose={handleClosePasswordReset}
        onConfirm={handleConfirmPasswordReset}
        title="Reset Admin Password"
        message={`Send a temporary password to ${passwordResetTarget?.admin?.email || "this admin"} for ${passwordResetTarget?.customer?.name || "this customer"}?`}
        confirmText={isResettingPassword ? "Sending..." : "Send Reset"}
        cancelText="Cancel"
        type="default"
        confirmDisabled={isResettingPassword}
      >
        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
          <div className="font-semibold text-slate-900">
            {passwordResetTarget?.admin?.username || "Admin user"}
          </div>
          <div className="mt-1 text-slate-500">
            {passwordResetTarget?.admin?.email || "No registered email"}
          </div>
          <div className="mt-3 text-xs leading-5 text-slate-500">
            The current password will be replaced after the email is sent successfully.
          </div>
        </div>
      </DeleteModal>

      <CustomerBranchesModal
        customer={branchCustomer}
        isOpen={Boolean(branchCustomer)}
        onClose={() => setBranchCustomer(null)}
      />
    </div>
  );
};

export default CustomerList;
